import React, { useState, useEffect } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { OUTLINE_BTN, CARD_TITLE } from "../utils/styles";

// TopoJSON world atlas URL for react-simple-maps
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Global cached map data to prevent redundant network fetches on re-mounts
let cachedGeoData = null;

// Internal CountryMap sub-component
const CountryMap = React.memo(({ countryPerformance = [] }) => {
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [geoData, setGeoData] = useState(cachedGeoData);

  useEffect(() => {
    if (cachedGeoData) return;
    let isMounted = true;
    fetch(geoUrl)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then((data) => {
        cachedGeoData = data;
        if (isMounted) setGeoData(data);
      })
      .catch((err) => {
        console.error("Error loading map data:", err);
      });
    return () => { isMounted = false; };
  }, []);

  // Normalize a country name for fuzzy matching between map geo data and API names
  const normalizeName = (name = "") => name.toLowerCase().trim();

  // Build a lookup map from normalized country name -> API record for O(1) access
  const countryLookup = countryPerformance.reduce((acc, item) => {
    if (item.countryName) {
      acc[normalizeName(item.countryName)] = item;
    }
    return acc;
  }, {});

  // Map geo property names to normalized API names (handles geo naming differences)
  const resolveApiRecord = (geo) => {
    const geoName = normalizeName(geo.properties?.name || "");
    // Direct match
    if (countryLookup[geoName]) return countryLookup[geoName];
    // Handle common geo naming aliases
    const aliases = {
      "united states of america": "united states",
      "us": "united states",
      "uk": "united kingdom",
      "great britain": "united kingdom",
    };
    const aliasedName = aliases[geoName];
    if (aliasedName && countryLookup[aliasedName]) return countryLookup[aliasedName];
    // Partial match: check if any API country name is contained within the geo name or vice versa
    const apiKeys = Object.keys(countryLookup);
    const partialMatch = apiKeys.find(
      (key) => geoName.includes(key) || key.includes(geoName)
    );
    return partialMatch ? countryLookup[partialMatch] : null;
  };

  const getCountryColor = (pct = 0) => {
    if (pct >= 41.0) return "#10B981"; // Emerald Green - high conversion
    if (pct >= 38.0) return "#3B82F6"; // Electric Blue - medium conversion
    return "#6f42c1"; // Theme purple - standard
  };

  return (
    <div
      className="position-relative w-100 d-flex flex-column align-items-center justify-content-center"
      style={{ overflow: "hidden" }}
    >
      {/* Tooltip Overlay */}
      <div
        style={{
          minHeight: "26px",
          transition: "all 0.2s ease",
          opacity: hoveredCountry ? 1 : 0,
          visibility: hoveredCountry ? "visible" : "hidden",
          transform: hoveredCountry ? "translateY(0)" : "translateY(-4px)",
          backgroundColor: "rgba(24, 28, 50, 0.85)",
          backdropFilter: "blur(4px)",
          borderRadius: "6px",
          padding: "4px 10px",
          fontSize: "0.75rem",
          color: "#ffffff",
          fontWeight: "600",
          zIndex: 10,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
          marginBottom: "8px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        {hoveredCountry && (
          <span>
            <strong style={{ color: "#8A58EC" }}>{hoveredCountry.country}</strong>: {hoveredCountry.stats}
          </span>
        )}
      </div>

      <ComposableMap
        projectionConfig={{ rotate: [-10, 0, 0], scale: 68, center: [0, 6] }}
        width={340}
        height={180}
        viewBox="0 0 340 180"
        style={{ width: "100%", height: "auto", maxHeight: "150px", overflow: "hidden" }}
      >
        {geoData && (
          <Geographies geography={geoData}>
            {({ geographies }) =>
              geographies
                .filter((geo) => {
                  const name = geo.properties?.name || "";
                  return !name.toLowerCase().includes("antarctica");
                })
                .map((geo) => {
                  const apiRecord = resolveApiRecord(geo);
                  const isHighlighted = !!apiRecord;
                  const pct = apiRecord?.callConcludedPercentage || 0;
                  return (
                    <Geography
                      key={geo.rrd3Id || geo.key || geo.id || Math.random()}
                      geography={geo}
                      fill={isHighlighted ? getCountryColor(pct) : "#DFD7FC"}
                      stroke="#FFFFFF"
                      strokeWidth={0.5}
                      onMouseEnter={() => {
                        if (isHighlighted && apiRecord) {
                          setHoveredCountry({
                            country: apiRecord.countryName,
                            stats: `${(apiRecord.totalCall || 0).toLocaleString()} calls | ${pct.toFixed(2)}%`,
                          });
                        }
                      }}
                      onMouseLeave={() => setHoveredCountry(null)}
                      style={{
                        default: { outline: "none", transition: "fill 0.3s ease" },
                        hover: {
                          fill: isHighlighted ? "#5c2d91" : "#C5B2FF",
                          outline: "none",
                          cursor: isHighlighted ? "pointer" : "default",
                        },
                        pressed: { outline: "none" },
                      }}
                    />
                  );
                })
            }
          </Geographies>
        )}
      </ComposableMap>
    </div>
  );
});

// Main CountryPerformance component — manages its own showAllCountries state
const CountryPerformance = ({ stats }) => {
  const [showAllCountries, setShowAllCountries] = useState(false);

  const getProgressColor = (pct) => {
    if (pct >= 41.0) return "#10B981"; // Emerald Green
    if (pct >= 38.0) return "#3B82F6"; // Electric Blue
    return "#6f42c1"; // Theme Purple
  };

  const countries = stats?.countryPerformance || [];

  // Always push the "Unknown" country to the very end of the list
  const rearrangedCountries = [
    ...countries.filter(c => c.countryName !== "Unknown"),
    ...countries.filter(c => c.countryName === "Unknown")
  ];

  const hasCountries = rearrangedCountries.length > 0;
  const countriesToRender = hasCountries
    ? rearrangedCountries
    : [{ countryName: "No Data", totalCall: 0, callConcluded: 0, callConcludedPercentage: 0 }];

  return (
    <div className="card card-custom h-100 shadow-sm border-0 bg-white rounded-lg p-5 p-md-6">
      <div className="card-header border-0 p-0 mb-5">
        <h3
          className="card-title font-weight-bolder text-dark font-size-h5 m-0"
          style={CARD_TITLE}
        >
          Country Performance
        </h3>
      </div>
      <div className="card-body p-0">
        <div className="row align-items-start">
          {/* Left side: World Map & View All Countries button */}
          <div className="col-12 col-xl-5 d-flex flex-column align-items-center justify-content-center border-right-xl pr-xl-4">
            <div
              className="w-100 mb-4 d-flex align-items-center justify-content-center"
              style={{ filter: "drop-shadow(0px 8px 16px rgba(111, 66, 193, 0.06))" }}
            >
              <CountryMap countryPerformance={countries} />
            </div>
            {rearrangedCountries.length > 5 && (
              <button
                type="button"
                onClick={() => setShowAllCountries(!showAllCountries)}
                className="btn btn-outline-primary px-4 py-2 rounded-lg font-weight-bolder"
                style={{ ...OUTLINE_BTN, fontSize: "0.8rem" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#6f42c1";
                  e.currentTarget.style.color = "#ffffff";
                  e.currentTarget.style.borderColor = "#6f42c1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#FBF9FF";
                  e.currentTarget.style.color = "#6f42c1";
                  e.currentTarget.style.borderColor = "#EFEBFC";
                }}
              >
                {showAllCountries ? "View Less" : "View All Countries"}
              </button>
            )}
          </div>

          {/* Right side: Country Performance Table */}
          <div className="col-12 col-xl-7 mt-4 mt-xl-0 pl-xl-4">
            <div
              style={{
                maxHeight: showAllCountries ? "260px" : "none",
                overflowY: showAllCountries ? "auto" : "visible",
                overflowX: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#6f42c1 transparent",
              }}
            >
              <table
                className="table table-borderless table-vertical-center m-0"
                style={{ width: "100%" }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr
                    className="text-dark font-weight-boldest border-bottom border-light"
                    style={{ fontSize: "0.70rem", letterSpacing: "0.7px", color: "#A1A5B7", backgroundColor: "#ffffff" }}
                  >
                    <th className="pl-0 pb-3" style={{ backgroundColor: "#ffffff", zIndex: 10 }}>
                      Country
                    </th>
                    <th className="text-center pb-3" style={{ backgroundColor: "#ffffff", zIndex: 10 }}>
                      Total Calls
                    </th>
                    <th className="text-center pb-3" style={{ backgroundColor: "#ffffff", zIndex: 10 }}>
                      Concluded Calls
                    </th>
                    <th className="text-right pb-3" style={{ backgroundColor: "#ffffff", zIndex: 10 }}>
                      Concluded %
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {countriesToRender
                    .filter((row, idx) => (showAllCountries ? true : idx < 5))
                    .map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-bottom border-light-soft"
                        style={{ transition: "all 0.25s ease-in-out", cursor: "pointer" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "rgba(110, 66, 193, 0.03)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <td
                          className="pl-0 py-3 font-weight-boldest text-dark"
                          style={{
                            fontSize: "0.85rem",
                            color: "#181C32",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: "1.25",
                            maxWidth: "125px"
                          }}
                        >
                          {row.countryName}
                        </td>
                        <td className="text-center py-3 font-weight-bolder text-dark-75" style={{ fontSize: "0.85rem", color: "#3F4254", whiteSpace: "nowrap" }}>
                          {(row.totalCall || 0).toLocaleString()}
                        </td>
                        <td className="text-center py-3 font-weight-bolder text-dark-75" style={{ fontSize: "0.85rem", color: "#3F4254", whiteSpace: "nowrap" }}>
                          {(row.callConcluded || 0).toLocaleString()}
                        </td>
                        <td className="text-right py-3 font-size-sm" style={{ whiteSpace: "nowrap" }}>
                          <div className="d-flex align-items-center justify-content-end">
                            <span
                              className="font-weight-boldest text-dark mr-2"
                              style={{ fontSize: "0.82rem", minWidth: "38px", color: "#181C32" }}
                            >
                              {row.callConcludedPercentage || 0}%
                            </span>
                            <div
                              className="progress"
                              style={{ height: "6px", width: "40px", flex: "0 0 40px", backgroundColor: "#F3F4F6", borderRadius: "10px" }}
                            >
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                  width: `${row.callConcludedPercentage || 0}%`,
                                  backgroundColor: getProgressColor(row.callConcludedPercentage || 0),
                                  borderRadius: "10px",
                                  transition: "width 0.4s ease",
                                }}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CountryPerformance);
